import { Observable, ReplaySubject } from '@reactivex/rxjs';
import { Gestures } from 'rxjs-gestures';
import { $atom, $value } from '@graphistry/falcor-json-graph';

import hoistStatics from 'react-recompose/hoistStatics';
import mapPropsStream from 'react-recompose/mapPropsStream';
import createEventHandler from 'react-recompose/createEventHandler';

// TODO: save deleted filter of other type
function resetBrushAfterModeSwitch(props) {
    return props
        .map(props => [props.timebar?.mode, props])
        .distinctUntilChanged((x, y) => x[0] === y[0])
        .filter(([_, { $falcor }]) => $falcor)
        .flatMap(([_, { $falcor }]) => Observable.merge(
            commitBrushFilters($falcor, []),
            commitBrushPlay($falcor, []),
        ));
}

export const withBrushFilters = hoistStatics(mapPropsStream((propsStream) => {

    const props = Observable.from(propsStream).multicast(() => new ReplaySubject(1)).refCount();
    const { handler: onBrushFilterStart, stream: filterBrushStarts } = createEventHandler();
    const { handler: onFilterBrushRemoved, stream: filterBrushDeletes } = createEventHandler();
    const { handler: onFilterBrushTimeChange, stream: filterBrushChanges } = createEventHandler();
    const newFilterBrushes = Gestures
        .pan(filterBrushStarts.filter((e) => e.shiftKey && !e.button))
        .repeat().exhaustMap(createFilterBrush.bind(null, props));

    return props.publish((props) => props.merge(
        resetBrushAfterModeSwitch(props),
        newFilterBrushes,
        updateFilterBrush(props, filterBrushChanges),
        removeFilterBrush(props, filterBrushDeletes)
    ))
        .map((props) => ({ ...props, onBrushFilterStart, onFilterBrushTimeChange, onFilterBrushRemoved }));
}));

function createFilterBrush(props, drags) {

    let brushIndex, brushRanges;

    return drags
        .withLatestFrom(props)
        .publish((dragsWithProps) => Observable.merge(
            dragsWithProps.take(1).map(addBrush),
            dragsWithProps.skip(1).map(updateBrush),
            dragsWithProps.takeLast(1).map(commitBrush)
        ))
        .switch()
        .filter(x => x.constructor === Array && x.length === 2)
        .map(([range, props]) => ({ ...props, range: [...range], isBrushFiltering: true }));

    function addBrush([point, props]) {
        const zoomScale = props.zoomScale;
        const t = +zoomScale.invert(point.xOrigin);
        brushRanges = [...(props.range || []), t, t];
        brushIndex = Math.floor(brushRanges.length / 2) - 1;
        return Observable.of([brushRanges, props]);
    }

    function updateBrush([point, props]) {
        const x = point.xOrigin;
        const { $falcor } = props;
        const zoomScale = props.zoomScale;
        const x0 = point.movementXTotal <= 0 ? x + point.movementXTotal : x;
        const x1 = point.movementXTotal <= 0 ? x : x + point.movementXTotal;
        brushRanges[brushIndex * 2] = +zoomScale.invert(Math.min(x0, x1));
        brushRanges[brushIndex * 2 + 1] = +zoomScale.invert(Math.max(x0, x1));
        return $falcor
            .withoutDataSource()
            .set($value('range', [...brushRanges]))
            .ignoreElements()
            .startWith([brushRanges, props]);
    }

    function commitBrush([_, props]) {
        const { $falcor, timebar } = props;
        const x0 = brushRanges[brushIndex * 2];
        const x1 = brushRanges[brushIndex * 2 + 1];
        brushRanges[brushIndex * 2] = Math.min(x0, x1);
        brushRanges[brushIndex * 2 + 1] = Math.max(x0, x1);
        return timebar?.mode ? commitBrushPlay($falcor, brushRanges) : commitBrushFilters($falcor, brushRanges);
    }
}

function commitBrushFilters($falcor, brushRanges) {
    return $falcor.call('filter', [...brushRanges]).ignoreElements();
}

function commitBrushPlay($falcor, brushRanges) {
    return $falcor.set($value('timebar.interval', brushRanges ?? [])).ignoreElements();
}

function updateFilterBrush(props, brushes) {

    return brushes
        .withLatestFrom(props)
        .publish((updatesWithProps) => Observable.merge(
            updatesWithProps.filter(([{ type }]) => type !== 'done').map(updateBrush),
            updatesWithProps.filter(([{ type }]) => type === 'done').map(commitBrush)
        ))
        .switch()
        .map(([range, props]) => ({ ...props, range: [...range], isPanScrolling: true }));

    function updateBrush([brush, props]) {
        const { $falcor } = props;
        const index = brush.props.brushId;
        const range = [...(props.range || [])];
        range[index * 2] = brush.timeDomain[0];
        range[index * 2 + 1] = brush.timeDomain[1];
        return $falcor
            .withoutDataSource()
            .set($value('range', [...range]))
            .ignoreElements()
            .startWith([range, props]);
    }

    function commitBrush([brush, props]) {
        const { $falcor, timebar } = props;
        const index = brush.props.brushId;
        const range = [...(props.range || [])];
        range[index * 2] = brush.timeDomain[0];
        range[index * 2 + 1] = brush.timeDomain[1];
        return timebar?.mode ? commitBrushPlay($falcor, [...range]) : commitBrushFilters($falcor, [...range])
    }
}

function removeFilterBrush(props, brushes) {
    return brushes.withLatestFrom(props).flatMap(([brush, props]) => {
        const { $falcor, timebar } = props;
        const index = brush.props.brushId;
        const range = [...(props.range || [])];
        range.splice(index * 2, 2);
        return Observable.merge(
            timebar?.mode
                ? commitBrushPlay($falcor, [...range])
                : commitBrushFilters($falcor, [...range]),
            $falcor
                .withoutDataSource()
                .set($value('range', $atom([...range], { $timestamp: Date.now() })))
        )
            .ignoreElements()
            .startWith({ ...props, range: [...range], isPanScrolling: true });
    });
}
